/* global axios */
// Usa o axios GLOBAL do dashboard (window.axios = createAxios), que carrega o
// interceptor de autenticação — o import cru de 'axios' não leva o token e dá 401.
//
// Webphone BCVOZ (PABX Bravotech) carregado GLOBALMENTE: o SDK vem do CDN
// (jsDelivr, sempre a última versão) e o widget flutuante fica visível em todas
// as páginas, como a BCVOZ recomendou. A sessão (vxToken + extensão SIP) é
// montada pelo nosso backend a partir do token da conta salvo na caixa.
//
// PRÓXIMA ETAPA: sessão POR AGENTE logado (hoje o backend usa o ramal primário
// da conta) e o formato oficial de sessão que a BCVOZ vai enviar.

let carregado = false; // injeta o SDK uma vez só por carregamento de página
const RESOLVED_URL =
  'https://data.jsdelivr.com/v1/packages/npm/@bcvoz/webphone/resolved';

async function buscarSessao(accountId) {
  const url = `/api/v1/accounts/${accountId}/kanban/bcvoz_tokens/webphone_session`;
  const { data } = await axios.get(url);
  return data?.data || null; // { api_base, session } ou null
}

async function injetarSdk() {
  // fetch nativo (não o axios do dashboard): é cross-origin pro CDN e não deve
  // levar o interceptor de auth nem baseURL.
  let version;
  try {
    const r = await fetch(RESOLVED_URL, { cache: 'no-store' });
    version = (await r.json())?.version;
  } catch (e) {
    version = null;
  }
  const src = version
    ? `https://cdn.jsdelivr.net/npm/@bcvoz/webphone@${version}/dist/bcvoz.umd.js`
    : 'https://cdn.jsdelivr.net/npm/@bcvoz/webphone/dist/bcvoz.umd.js';
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.async = true;
    s.onload = resolve;
    s.onerror = () => reject(new Error('BCVoz: falha ao carregar do CDN'));
    document.head.appendChild(s);
  });
}

export function useBcvozWebphone() {
  // Carrega e inicializa o webphone global. Idempotente e silencioso: se o
  // módulo bcvoz não está ligado ou não há sessão, não faz nada.
  const loadBcvozWebphone = async accountId => {
    if (carregado || !accountId || typeof window === 'undefined') return;

    let cfg;
    try {
      cfg = await buscarSessao(accountId);
    } catch (e) {
      return; // sem endpoint/integração → silencioso
    }
    // Sem integração ativa → não carrega. COM integração mas SEM sessão (o agente
    // não casou com um usuário BCVOZ) → carrega o widget SEM logar (login manual).
    if (!cfg?.api_base) return;

    // A sessão vem do POST /api/pabx/login: o token de sessão é `pabxToken`.
    // O SDK espera `vxToken` — mapeia um no outro quando necessário.
    const session = cfg.session
      ? {
          ...cfg.session,
          vxToken: cfg.session.vxToken || cfg.session.pabxToken,
        }
      : null;

    carregado = true;
    try {
      if (!window.BCVoz) await injetarSdk();
      const opts = {
        apiBase: cfg.api_base,
        mode: 'srcdoc', // roda na nossa origem (CORS já liberado pela BCVOZ)
        open: false, // começa INVISÍVEL (sem janela)
        launcher: false, // SEM aba/botão flutuante — o widget só aparece em ligação
        position: 'bottom-right',
      };
      // Só passa sessão/refresh quando há login automático; sem match, o widget
      // abre deslogado e o agente faz o login dele no próprio webphone.
      if (session) {
        opts.session = session;
        opts.refreshSession = async () => {
          const nova = await buscarSessao(accountId);
          const s = nova?.session;
          return s ? { ...s, vxToken: s.vxToken || s.pabxToken } : {};
        };
      }
      window.BCVoz.init(opts);
      // Sem botão flutuante: o webphone abre SOZINHO quando há ligação — entrante
      // (call:incoming) ou ao discar pelo ticket (call:dialing, disparado por
      // BCVoz.call()). Fora de ligação, fica invisível. O agente fecha a janela
      // normalmente quando quiser.
      const abrirWebphone = () => {
        try {
          window.BCVoz.show();
        } catch (err) {
          /* widget ainda subindo — o próximo evento resolve */
        }
      };
      window.BCVoz.on('call:incoming', abrirWebphone);
      window.BCVoz.on('call:dialing', abrirWebphone);
      // PREWARM (com login automático): MONTA e REGISTRA o ramal já no carregamento,
      // ESCONDIDO. O iframe do webphone — e com ele o registro SIP no gateway WebRTC —
      // só nasce quando a janela abre; com `open:false`/`launcher:false` nada montava,
      // então o ramal ficava SEM registro e NÃO recebia ligação ociosa (o `call:incoming`
      // nunca disparava: ovo-galinha). show()+hide() no mesmo tick cria o iframe e o deixa
      // invisível — ele conclui o login/registro em segundo plano e some, sem flash. Na
      // ligação, o call:incoming/dialing chama show() e a janela aparece.
      if (session) {
        const prewarm = () => {
          try {
            window.BCVoz.show();
            window.BCVoz.hide();
          } catch (err) {
            /* ponte do iframe ainda subindo — a retentativa resolve */
          }
        };
        prewarm();
        setTimeout(prewarm, 1500);
      }
      // SEM match: o webphone guarda a última sessão em storage e volta logado
      // sozinho. Força o logout pra cair na TELA DE LOGIN do próprio widget.
      if (!session && typeof window.BCVoz.logout === 'function') {
        const forcarLogout = () => {
          try {
            window.BCVoz.logout();
          } catch (err) {
            /* bridge ainda subindo — a 2ª tentativa resolve */
          }
        };
        forcarLogout();
        setTimeout(forcarLogout, 1500); // garante após a ponte do iframe subir
      }
    } catch (e) {
      carregado = false; // deixa tentar de novo numa próxima navegação
      // eslint-disable-next-line no-console
      console.error('[BCVOZ webphone]', e?.message || e);
    }
  };

  return { loadBcvozWebphone };
}
